import { useState, useMemo } from "react";
import { useExpiredProducts, useExpiredProductsStats, useRunExpiredProductsCheck } from "@/hooks/useExpiredProducts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCw, Search, AlertTriangle, Package, MapPin, XCircle, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { toast } from "sonner";
import { MatchDebugDialog } from "@/components/expired-products/MatchDebugDialog";
import { AlternativeBadge } from "@/components/expired-products/AlternativeBadge";

const issueTypeLabels: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  no_match: { label: "Geen Match", color: "destructive", icon: <XCircle className="h-4 w-4" /> },
  locatie_nv: { label: "Locatie NV", color: "warning", icon: <MapPin className="h-4 w-4" /> },
  locatie_vervallen: { label: "Vervallen", color: "destructive", icon: <AlertTriangle className="h-4 w-4" /> },
  naam_zzz: { label: "ZZZ Product", color: "secondary", icon: <AlertCircle className="h-4 w-4" /> },
};

const ExpiredProducts = () => {
  const [issueFilter, setIssueFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortByAlternative, setSortByAlternative] = useState<"asc" | "desc" | null>("desc");

  const { data: products, isLoading: productsLoading, refetch } = useExpiredProducts(issueFilter);
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useExpiredProductsStats(issueFilter);
  const runCheck = useRunExpiredProductsCheck();

  const handleRunCheck = async () => {
    try {
      toast.loading("Vervallen producten check wordt uitgevoerd...", { id: "expired-check" });
      const result = await runCheck.mutateAsync();
      toast.success(
        `Check voltooid: ${result.summary?.total_issues || 0} problemen gevonden`,
        { id: "expired-check" }
      );
      // Refetch both products AND stats
      await Promise.all([refetch(), refetchStats()]);
    } catch (error) {
      console.error("Error running check:", error);
      toast.error("Fout bij uitvoeren check", { id: "expired-check" });
    }
  };

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let result = products?.filter((product) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        product.title?.toLowerCase().includes(query) ||
        product.sku?.toLowerCase().includes(query) ||
        product.barcode?.toLowerCase().includes(query) ||
        product.matched_product_id?.toLowerCase().includes(query) ||
        product.matched_product_naam?.toLowerCase().includes(query)
      );
    }) || [];

    // Sort by alternative if enabled
    if (sortByAlternative) {
      result = [...result].sort((a, b) => {
        const aHasAlt = a.alternative_product_naam ? 1 : 0;
        const bHasAlt = b.alternative_product_naam ? 1 : 0;
        return sortByAlternative === "desc" ? bHasAlt - aHasAlt : aHasAlt - bHasAlt;
      });
    }

    return result;
  }, [products, searchQuery, sortByAlternative]);

  const toggleAlternativeSort = () => {
    if (sortByAlternative === null) setSortByAlternative("desc");
    else if (sortByAlternative === "desc") setSortByAlternative("asc");
    else setSortByAlternative(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Vervallen Producten</h1>
          <p className="text-muted-foreground">
            Lightspeed producten met problemen in voorraad_live
          </p>
        </div>
        <Button onClick={handleRunCheck} disabled={runCheck.isPending}>
          {runCheck.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Check Nu Uitvoeren
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Totaal</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats?.total || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Geen Match</CardTitle>
            <XCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats?.no_match || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Locatie NV</CardTitle>
            <MapPin className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">
              {statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats?.locatie_nv || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vervallen</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats?.locatie_vervallen || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ZZZ</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats?.naam_zzz || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Met Alternatief</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats?.with_alternative || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Last Check Info */}
      {stats?.last_check && (
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">
              Laatste controle:{" "}
              <span className="font-medium text-foreground">
                {format(new Date(stats.last_check), "d MMMM yyyy 'om' HH:mm", { locale: nl })}
              </span>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Producten</CardTitle>
          <CardDescription>
            Filter en zoek in problematische producten
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Zoek op SKU, barcode, titel..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={issueFilter} onValueChange={setIssueFilter}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Filter op type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle types</SelectItem>
                <SelectItem value="no_match">Geen Match</SelectItem>
                <SelectItem value="locatie_nv">Locatie NV</SelectItem>
                <SelectItem value="locatie_vervallen">Vervallen</SelectItem>
                <SelectItem value="naam_zzz">ZZZ Product</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardContent className="pt-6">
          {productsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !filteredAndSortedProducts?.length ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Package className="h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-4 text-lg font-semibold">Geen producten gevonden</h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? "Probeer een andere zoekterm"
                  : "Voer de check uit om problematische producten te vinden"}
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 select-none"
                      onClick={toggleAlternativeSort}
                    >
                      <div className="flex items-center gap-1">
                        Alternatief
                        {sortByAlternative === "desc" ? (
                          <ArrowDown className="h-4 w-4 text-primary" />
                        ) : sortByAlternative === "asc" ? (
                          <ArrowUp className="h-4 w-4 text-primary" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead>Titel (Lightspeed)</TableHead>
                    <TableHead>Match Info</TableHead>
                    <TableHead>Gecontroleerd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedProducts.map((product) => {
                    const issueInfo = issueTypeLabels[product.issue_type];
                    return (
                      <TableRow key={product.id}>
                        <TableCell>
                          <Badge variant={issueInfo?.color as "default" | "destructive" | "secondary" | "outline" || "default"} className="gap-1">
                            {issueInfo?.icon}
                            {issueInfo?.label || product.issue_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {product.sku || "-"}
                          {product.barcode && (
                            <div className="text-xs text-muted-foreground">BC: {product.barcode}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <AlternativeBadge
                            productNaam={product.alternative_product_naam}
                            locatie={product.alternative_locatie}
                            voorraad={product.alternative_voorraad}
                            matchType={product.alternative_match_type}
                            sku={product.alternative_sku}
                            eenheid={product.alternative_eenheid}
                          />
                        </TableCell>
                        <TableCell className="max-w-[300px]">
                          <div className="truncate font-medium" title={product.title}>
                            {product.title}
                          </div>
                        </TableCell>
                        <TableCell>
                          {product.matched_product_id ? (
                            <div className="text-sm">
                              <div className="font-mono">{product.matched_product_id}</div>
                              <div className="text-xs text-muted-foreground">
                                Locatie: {product.matched_locatie || "-"}
                              </div>
                              {product.matched_product_naam && (
                                <div className="text-xs text-muted-foreground truncate max-w-[200px]" title={product.matched_product_naam}>
                                  {product.matched_product_naam}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">-</span>
                              <MatchDebugDialog debug={product.match_debug} issueType={product.issue_type} />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(product.checked_at), "dd-MM-yyyy HH:mm")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {filteredAndSortedProducts && filteredAndSortedProducts.length > 0 && (
            <div className="mt-4 text-sm text-muted-foreground">
              {filteredAndSortedProducts.length} {filteredAndSortedProducts.length === 1 ? "product" : "producten"} gevonden
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpiredProducts;
